import { Spinner } from '@atlas-design-system/react'
import { useQuery } from '@tanstack/react-query'
import { SERVICES } from 'common/api'
import { TRAVELPORT_SUPPLIER_CODE } from 'common/constants'
import { Permission, UserRole } from 'common/enums'
import { pickUniqueSipplierCodes } from 'common/helpers'
import { AppRoutes } from 'common/routes/routes'
import { fetchUserDetails } from 'common/services/auth.service'
import { fetchCdnToken } from 'common/services/cdn.service'
import { AuthUser } from 'common/types'
import { envConfig } from 'config/config'
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

export type AuthContextType = {
  user: AuthUser | null
  TVPAuthorization: string | null
  logOut: () => void
  hasPermissionTo: (permission: Permission) => boolean
  isAdminUser: boolean
  isTravelportUser: boolean
  userSupplierCodes: string[]
}

export const AuthContext = createContext<AuthContextType>({
  user: null,
  TVPAuthorization: null,
  logOut: () => ({}),
  hasPermissionTo: () => false,
  isAdminUser: false,
  isTravelportUser: false,
  userSupplierCodes: [],
})

export const useAuth = (): AuthContextType => {
  return useContext(AuthContext)
}

export const AuthContextProvider: React.FC<{
  children: ReactNode
}> = ({ children }) => {
  const [TVPAuthorization, setTVPAuthorization] = useState<string | null>(null)

  // Allow public pages like cookie policy to be accessed without authentication
  const publicPaths = ['/error', '/cookie-policy', '/cookie-policy-us']
  const isPublicPath = publicPaths.some((path) =>
    globalThis.location.pathname.includes(path),
  )

  const { data: user = null } = useQuery({
    queryKey: ['getUser'],
    queryFn: () =>
      fetchUserDetails().catch((error) => {
        // Redirect to login only if the user is unauthorized
        if (error.message.includes('401')) {
          globalThis.location.assign(
            `${envConfig.config.API_BASE_URL}/${SERVICES.AUTH_SERVICE}/`,
          )
          return null
        }

        throw new Error('Failed_Retrieving_User_Details')
      }),
    enabled: !isPublicPath, // Only fetch user details if not on a public path
  })

  const logOut = useCallback(() => {
    globalThis.location.assign(
      `${envConfig.config.API_BASE_URL}/${SERVICES.AUTH_SERVICE}/logout`,
    )
  }, [])

  const hasPermissionTo = useCallback(
    (permission: Permission): boolean => {
      if (!user) {
        return false
      }

      return user.permissions.includes(permission)
    },
    [user],
  )

  const hasImageWritePermissions = useMemo(() => {
    if (!user) {
      return false
    }

    return (
      hasPermissionTo(Permission.WRITE_BRANDS) ||
      hasPermissionTo(Permission.WRITE_ANCILLARIES) ||
      hasPermissionTo(Permission.WRITE_DASHBOARD) ||
      hasPermissionTo(Permission.WRITE_SEGMENT_BRANDING)
    )
  }, [hasPermissionTo, user])

  useEffect(() => {
    if (user && !TVPAuthorization && hasImageWritePermissions) {
      fetchCdnToken()
        .then((token) => {
          sessionStorage.setItem('TVPAuthorization', token)
          setTVPAuthorization(token)
        })
        .catch(() => {
          // CDN token retrieval failed — non-critical, continue without it
        })
      setTimeout(
        () => setTVPAuthorization(null),
        30 * 1000 * 60, // 30 min
      )
    }
  }, [TVPAuthorization, user, hasImageWritePermissions])

  const isAdminUser = useMemo(() => {
    if (!user) {
      return false
    }

    return user.roles.includes(UserRole.MMP_ADMIN)
  }, [user])

  const userSupplierCodes = useMemo(() => {
    if (!user) {
      return []
    }

    const { supplierCodes } = user
    return pickUniqueSipplierCodes(supplierCodes)
  }, [user])

  const isTravelportUser = useMemo(() => {
    if (!user) {
      return false
    }

    return userSupplierCodes.includes(TRAVELPORT_SUPPLIER_CODE)
  }, [user, userSupplierCodes])

  const contextValue = useMemo(
    () => ({
      user,
      TVPAuthorization,
      logOut,
      hasPermissionTo,
      isAdminUser,
      isTravelportUser,
      userSupplierCodes,
    }),
    [
      user,
      TVPAuthorization,
      logOut,
      hasPermissionTo,
      isAdminUser,
      isTravelportUser,
      userSupplierCodes,
    ],
  )

  if (isPublicPath) {
    return <AppRoutes />
  } else {
    return (
      <AuthContext.Provider value={contextValue}>
        {user && children}
        {!user && (
          <div>
            <Spinner primaryLabel="Logging you in..." />
          </div>
        )}
      </AuthContext.Provider>
    )
  }
}
